import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useContext, useMemo } from 'react';
import {
    INSIGHTS_QUERY_KEY,
    INSIGHTS_SETTINGS_KEY,
    INSIGHT_CATEGORIES_QUERY_KEY,
    InsightCategoryType,
    InsightGroup,
    getInsightCategories,
    getInsightSettings,
    getInsights,
} from '@/queries/insights';
import { AppContext } from '@/contexts/app-context';
import { useGlobalQueries } from './use-global-queries';
import { InsightPost, InsightProps } from '@/isomorphic/components/wp-blocks/insights/insight';

const PER_PAGE = 10;

export type CurrentFiltersType = {
    page: number;
    count: number;
    group: InsightGroup;
    currentInsight: InsightPost | null;
    term: InsightCategoryType | null;
};

export type GoToKey = 'term' | 'group' | 'page';
export type GoToCallbackType = (key: GoToKey, value: string) => void;

export type SortedTermsType = Record<InsightGroup, InsightCategoryType[]>;

export type InsightListItem = {
    id: number;
    slug: string;
    title: string;
    preamble: string;
    image?: {
        src: string;
        alt_text: string;
        originalSize: { width: number; height: number };
    };
    terms: InsightCategoryType[];
};

export function useInsights() {
    const location = useLocation();
    const navigate = useNavigate();
    const context = useContext(AppContext);
    const { pageQuery, findAllBlocksOfType } = useGlobalQueries();
    const [searchParams, setSearchParams] = useSearchParams();

    // Utils
    const getTermFromId = (termId?: number | null) => (termId ? (insightCategoriesData.data?.find(({ id }) => id === termId) ?? null) : null);
    const getFromTo = (currentPage: number, length: number): [number, number] => {
        const to = currentPage * PER_PAGE;
        return [currentPage === 1 ? 0 : (currentPage - 1) * PER_PAGE, to > length ? length : to];
    };
    const getTermsFromTermIds = (termIds: number[]) => {
        const terms: InsightCategoryType[] = [];

        termIds.forEach(termId => {
            const term = getTermFromId(termId);
            if (term) {
                terms.push(term);
            }
        });

        return terms;
    };

    const insightsSettings = useQuery({
        queryKey: [INSIGHTS_SETTINGS_KEY],
        queryFn: () => getInsightSettings(context),
        placeholderData: keepPreviousData,
    });

    const insightCategoriesData = useQuery({
        queryKey: [INSIGHT_CATEGORIES_QUERY_KEY],
        queryFn: () => getInsightCategories(context),
        placeholderData: keepPreviousData,
    });

    const insightsData = useQuery({
        queryKey: [INSIGHTS_QUERY_KEY],
        queryFn: () => getInsights(context),
        placeholderData: keepPreviousData,
    });

    const allInsights = useMemo(() => insightsData.data || [], [insightsData.data]);
    const allTerms = useMemo(() => insightCategoriesData.data || [], [insightCategoriesData.data]);
    const basePath = useMemo(() => insightsSettings.data?.slug?.replace(/\/$/, '') || '/resurs-insights', [insightsSettings.data]);
    const showAllTermId = useMemo(() => insightsSettings.data?.show_all_category ?? null, [insightsSettings.data]);
    const currentInsight = useMemo(() => {
        if (!pageQuery.data?.resursbanken_post) {
            return null;
        }

        const posts = findAllBlocksOfType<InsightProps>('resurs/resursbanken-post');
        if (!posts.length) {
            return null;
        }

        return posts[0]?.attributes?.transformAttributes?.post || null;
    }, [pageQuery.data, allInsights]);

    const currentPage = useMemo(() => parseInt(searchParams.get('page') || '1'), [searchParams]);

    const currentTerm = useMemo(() => {
        const pathname = location.pathname.replace(/\/$/, '');
        if (!basePath || basePath === pathname) {
            return null;
        }

        const termSlug = pathname.replace(`${basePath}/`, '');
        return allTerms.find(term => term.slug === termSlug) || null;
    }, [location, basePath, allTerms, currentInsight]);

    const currentGroup = useMemo(() => {
        if (searchParams.has('group')) {
            const group = searchParams.get('group');
            if (group === 'private' || group === 'corporate') {
                return group as InsightGroup;
            }
        }

        if (currentInsight) {
            return currentInsight?.group?.[0] || 'private';
        }

        return 'private';
    }, [searchParams, currentInsight]);

    const filteredInsights = useMemo(
        () =>
            [...allInsights].filter(item => {
                if (currentGroup && !item.group.includes(currentGroup)) {
                    return false;
                }

                return currentTerm ? item.categories.includes(currentTerm.id) : true;
            }),
        [allInsights, currentGroup, currentTerm]
    );
    const currentCount = useMemo(() => filteredInsights.length, [filteredInsights]);

    const currentInsights = useMemo(
        () =>
            [...filteredInsights]
                .slice(...getFromTo(currentPage, currentCount))
                .map(({ id, main_image: image, short_title: shortTitle, post_title: postTitle, short_preamble: preamble, categories, slug }) => ({
                    id,
                    slug,
                    title: shortTitle || postTitle,
                    image,
                    preamble,
                    terms: getTermsFromTermIds(categories),
                })) ?? [],
        [filteredInsights, currentPage, currentCount]
    );

    const sortedTerms = useMemo(() => {
        const privateTerms = new Set<number>();
        const corporateTerms = new Set<number>();

        allInsights.forEach(({ categories, group }) => {
            if (group.includes('corporate')) {
                categories.forEach(term => corporateTerms.add(term));
            }
            if (group.includes('private')) {
                categories.forEach(term => privateTerms.add(term));
            }
        });

        return {
            private: [...privateTerms].map(termId => allTerms.find(({ id }) => id === termId)).filter(Boolean) as InsightCategoryType[],
            corporate: [...corporateTerms].map(termId => allTerms.find(({ id }) => id === termId)).filter(Boolean) as InsightCategoryType[],
        };
    }, [allInsights, allTerms]);

    const termMessage = useMemo(() => {
        const term = currentTerm ?? allTerms.find(({ id }) => id === showAllTermId);
        if (!term || !term?.messages?.length) {
            return null;
        }

        if (term.id === showAllTermId) {
            return term.messages.find(message => (currentGroup === 'corporate' ? message.show_corporate : message.show_private));
        }

        return term.messages[0];
    }, [allTerms, currentTerm, currentGroup, showAllTermId]);

    const goTo = (key: GoToKey, value: string) => {
        if (key === 'page') {
            setSearchParams({ page: value, group: currentGroup });
            return;
        }

        const to = key === 'group' ? `${basePath}?page=1&group=${value}` : `${basePath}/${value}?page=1&group=${currentGroup}`;
        navigate(to);
    };

    return {
        goTo,
        basePath,
        allTerms,
        sortedTerms,
        termMessage,
        filteredInsights,
        currentFilters: {
            currentInsight,
            insights: currentInsights,
            page: currentPage,
            perPage: PER_PAGE,
            count: currentCount,
            term: currentTerm,
            group: currentGroup,
        },
    };
}
